import { Intro, StatStrip, Friction, HeroStat, Callout, Takeaway, SectionLead, Disclaimer, Shot } from './components.jsx';

export const meta = {
  slug: 'picmondoo',
  brand: 'Picmondoo',
  industry: 'Diamond Painting · Shopify Plus',
  statNum: '$12,683',
  statDesc: 'Post-purchase upsell revenue · ~19× return on app cost',
  detail:
    'How Picmondoo turned the order page into a second storefront — earning $12,683 in upsell revenue and custom-built partial-delivery shipping logic for mixed in-stock and made-to-order baskets.',
  quote:
    '"I am very satisfied with both the app and the team at Account Editor. Not only does the app generate an additional €1–2k in revenue for us each month, but they also implemented a complex custom solution specifically tailored to our needs."',
  author: 'Manuel Reiter',
  role: 'Picmondoo® · Premium Diamond Painting · Germany',
  img: 'Picmondoo',
  hero: 'Picmondoo+Case+Study',
  image: '/images/case-studies/picmondoo-case-study.webp',
  readTime: '5 min read',
  date: 'May 25, 2026'
};

export default function Content() {
  return (
    <>
      <Intro>
        Make every order worth more. Picmondoo turned the order confirmation page into a second
        storefront — relevant accessory upsells, plus custom shipping logic built for baskets that
        mix in-stock items with made-to-order designs.
      </Intro>

      <StatStrip
        items={[
          { num: '$12,683', label: 'tracked upsell revenue', highlight: true },
          { num: '26,996', label: 'upsell offers shown' },
          { num: '1,241', label: 'paid upsells retained · 91%' },
          { num: '~19×', label: 'return on app cost' },
        ]}
        period="Aug 2025 – May 2026 · ~9 months"
      />

      <SectionLead eyebrow="About the merchant" title="Diamond painting kits, accessories, and made-to-order designs.">
        Picmondoo® designs premium diamond-painting kits in Germany — 1,200+ designs, plus pens,
        light pads, trays, storage, and made-to-order personalised pieces. ~6,000 orders a month
        on Shopify Plus, with baskets that often mix ready-to-ship and made-to-order items.
      </SectionLead>

      <SectionLead eyebrow="The upsell engine" title="Revenue from orders already won.">
        Kit buyers are ideal accessory customers — but only if you can reach them again without
        new ad spend. Account Editor put relevant upsells directly on the order confirmation page.
      </SectionLead>

      <HeroStat
        num="$12,683"
        label="tracked upsell revenue"
        period="Aug 2025 – May 2026"
        breakdown={[
          { label: 'Offers shown', num: '26,996', note: '~5% added by customers' },
          { label: 'Paid & retained', num: '1,241', note: '91% of added stayed paid' },
        ]}
      />

      <Shot
        src="/images/case-studies/picmondoo-upsell-offer.webp"
        alt="Picmondoo post-purchase upsell offer shown on the order status page after checkout"
        caption="Post-purchase upsell offers on the order status page — revenue from orders already won."
      />

      <Callout>
        ~<strong>19×</strong> return on app cost. Upsell revenue alone returned close to nineteen
        times what Picmondoo pays for Account Editor.
      </Callout>

      <SectionLead eyebrow="Built for how Picmondoo ships" title="When the basket mixes in-stock and made-to-order.">
        Standard shipping flows force an all-or-nothing decision on mixed baskets. Account Editor built
        custom logic — partial delivery and pay-the-difference upgrades — to match Picmondoo's
        exact fulfilment workflow.
      </SectionLead>

      <Friction
        question="Send me the accessories now — I'll wait for the made-to-order piece."
        oldWay="Standard flow holds the entire order until the made-to-order item is ready. Customer waits weeks for something already in stock."
        newWay='Customer chooses "Vorab-Lieferung" (advance delivery) — in-stock items ship now, made-to-order pieces follow when ready.'
        callout="Custom logic built for Picmondoo, in German, on the order page."
      />

      <Friction
        question="I need this faster — upgrade my shipping."
        oldWay="Mid-order shipping upgrade charges the full new rate, re-billing for shipping already paid."
        newWay="Pay-the-difference: customer pays only the delta between old and new shipping method — fair, simple, fast."
        callout="Customers chose paid shipping upgrades <strong>100+</strong> times."
      />

      <SectionLead eyebrow="The point" title="Not the feature. The answer.">
        When Picmondoo asked for partial delivery, pay-the-difference upgrades, and German-language
        wording — Account Editor built it. Most apps would have pointed at a roadmap.
      </SectionLead>

      <Takeaway footnote="Measured across the Picmondoo deployment · Aug 2025 – May 2026">
        <strong>$12,683</strong> in upsell revenue. <strong>~19×</strong> return on app cost.
        Smoother mixed-basket deliveries, fair shipping upgrades, and a localised experience tuned
        for the German market.
      </Takeaway>

      <Disclaimer>
        Figures cover Aug 2025 – May 2026. Upsell revenue and offer counts are measured directly
        from the Account Editor deployment. Return-on-cost is calculated against Picmondoo's Account Editor
        subscription over the same window. Custom shipping logic is built specifically for
        Picmondoo's fulfilment workflow.
      </Disclaimer>
    </>
  );
}
